import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { usePremium } from '@/hooks/usePremium';
import { hasFeatureInGroup } from '@/lib/premium';
import { supabase } from '@/lib/supabase';
import { fetchAllRows } from '@/lib/fetchAllRows';
import { fetchExchangeRates } from '@/api/currencies';
import {
  computeBudgetStatus,
  getPeriodBoundaries,
  isGroupBudgetEnabled,
} from '@/lib/budget';
import type { BudgetStatus, BudgetExpenseRow } from '@/lib/budget';
import type { GroupWithMembers } from '@/api/groups';

/**
 * Fetches the current budget status for a single group.
 * Returns `null` when the group's budget is disabled OR the viewer has
 * no premium access to budgets in this group (i.e. no premium member
 * present). Data in the DB stays intact — gating is purely viewing-time.
 */
export function useGroupBudget(group: GroupWithMembers | null | undefined) {
  const { user } = useAuth();
  const { tier } = usePremium();

  const hasPremiumAccess = !!group && hasFeatureInGroup(tier, 'group_budgets', group.members);
  const enabled = !!user && !!group && isGroupBudgetEnabled(group) && hasPremiumAccess;

  return useQuery<BudgetStatus | null>({
    queryKey: ['groupBudget', group?.id, group?.budget_period, group?.budget_start_day, group?.budget_amount_minor, hasPremiumAccess],
    queryFn: async () => {
      if (!group || !isGroupBudgetEnabled(group)) return null;

      const { start, end } = getPeriodBoundaries(
        group.budget_period as 'monthly' | 'total',
        group.created_at,
        group.budget_start_day
      );

      const [expenseRows, rates] = await Promise.all([
        fetchAllRows(
          supabase
            .from('expenses')
            .select('amount_minor_src, amount_minor_usd, currency_code_src, expense_date')
            .eq('group_id', group.id)
            .eq('_deleted', false)
            // expense_date is a timestamp: compare full instants so the last day is included
            .gte('expense_date', start.toISOString())
            .lte('expense_date', end.toISOString()),
        ),
        fetchExchangeRates(),
      ]);

      return computeBudgetStatus(group, expenseRows as BudgetExpenseRow[], rates);
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}
